import { Head, router } from "@inertiajs/react"
import { useForm } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

import LogForm from "./_form"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface Option {
  id: number
  name: string
}

interface LogData {
  id: number
  log_type: string
  content: string
  title: string
  request: string
  request_date: string | null
  log_date: string
  due_date: string | null
  results_notes: string
  assigned_to_id: number | null
  significant: boolean
  sensitive: boolean
  urgent: boolean
  customer_user_id: number | null
  vendor_id: number | null
  todo_category_id: number | null
  user_id: number | null
}

interface NotifiableUser {
  id: number
  name: string
}

interface Props {
  case_record: CaseRecord
  log: LogData
  users: Option[]
  todo_categories: Option[]
  can_create_restricted: boolean
  can_create_accounting: boolean
  errors?: Record<string, string[]>
  notifiable_users?: NotifiableUser[]
  case_manager?: NotifiableUser | null
}

export default function Edit({
  case_record,
  log,
  users,
  todo_categories,
  can_create_restricted,
  can_create_accounting,
  errors,
  notifiable_users,
  case_manager,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Logs", href: `/cases/${case_record.id}/logs` },
    { title: "Edit", href: `/cases/${case_record.id}/logs/${log.id}/edit` },
  ]

  const [notifyUserIds, setNotifyUserIds] = useState<number[]>([])

  const { data, setData, put, processing, transform } = useForm({
    log_type: log.log_type,
    content: log.content,
    title: log.title,
    request: log.request,
    request_date: log.request_date,
    log_date: log.log_date,
    due_date: log.due_date,
    results_notes: log.results_notes,
    assigned_to_id: log.assigned_to_id,
    significant: log.significant,
    sensitive: log.sensitive,
    urgent: log.urgent,
    customer_user_id: log.customer_user_id,
    vendor_id: log.vendor_id,
    todo_category_id: log.todo_category_id,
    user_id: log.user_id,
  })

  transform((data) => ({ case_log: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (data.log_type === "update" || data.log_type === "todo") {
      router.put(`/cases/${case_record.id}/logs/${log.id}`, {
        case_log: { ...data },
        notify_user_ids: notifyUserIds,
      })
    } else {
      put(`/cases/${case_record.id}/logs/${log.id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit Log - ${case_record.case_number}`} />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Edit Log</h1>
        <LogForm
          caseId={case_record.id}
          data={data}
          setData={setData}
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          submitLabel="Update Log"
          users={users}
          todo_categories={todo_categories}
          can_create_restricted={can_create_restricted}
          can_create_accounting={can_create_accounting}
          notifiableUsers={notifiable_users}
          caseManager={case_manager}
          notifyUserIds={notifyUserIds}
          onNotifyUserIdsChange={setNotifyUserIds}
        />
      </div>
    </AppLayout>
  )
}
